import React, { useState } from 'react';
import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';
import {
  useGetConversationsQuery,
  useGetConversationQuery,
  useSendMessageMutation,
} from 'state/api';
import { PageContainer } from 'components/shared';
import UserList from './userlist';
import Header from './header';
import ChatBox from './chatbox';
import MessageInput from './messageinput';

// Inbox: two-pane messaging view wired to the real conversations API.
//   • useGetConversationsQuery  → left pane (UserList)
//   • useGetConversationQuery   → selected thread (Header + ChatBox)
//   • useSendMessageMutation    → composer (MessageInput)
const Inbox = () => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [messageText, setMessageText] = useState('');

  const { data: conversationsData, isLoading: conversationsLoading } =
    useGetConversationsQuery({ search });

  const { data: conversationData, isFetching: conversationLoading } =
    useGetConversationQuery(selectedId, { skip: !selectedId });

  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();

  const conversations = conversationsData?.data ?? [];
  const conversation = conversationData?.data ?? null;
  const messages = conversation?.messages ?? [];

  const handleSend = async () => {
    const body = messageText.trim();
    if (!body || !selectedId) return;
    try {
      await sendMessage({ id: selectedId, body }).unwrap();
      setMessageText('');
    } catch (err) {
      // Keep the text so the user can retry on failure.
    }
  };

  return (
    <PageContainer title="Inbox" subtitle="Customer conversations and messages">
      <Grid container spacing={2} sx={{ height: '75vh' }}>
        {/* ── LEFT: conversation list ─────────────────────────────────── */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: theme.palette.background.alt,
            }}
          >
            <UserList
              conversations={conversations}
              loading={conversationsLoading}
              selectedId={selectedId}
              onSelect={setSelectedId}
              search={search}
              onSearchChange={setSearch}
            />
          </Paper>
        </Grid>

        {/* ── RIGHT: message thread ───────────────────────────────────── */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: theme.palette.background.alt,
            }}
          >
            {!selectedId ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5" color="text.secondary">
                  Select a conversation
                </Typography>
              </Box>
            ) : (
              <>
                <Header conversation={conversation} />
                <ChatBox messages={messages} loading={conversationLoading} />
                <MessageInput
                  value={messageText}
                  onChange={setMessageText}
                  onSend={handleSend}
                  sending={sending}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default Inbox;
